import React, { useEffect, useMemo, useState } from 'react';
import StaticPointRotator from './StaticPointRotator';

const RouteStatusDrawer = ({ routes, activeRouteIds, onToggleRoute, maxHeight = 200, points, trayConfig, mapRef, onOpenPointPopup }) => {
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  const isActive = (routeId) => {
    if (!activeRouteIds) return true;
    if (Array.isArray(activeRouteIds)) return activeRouteIds.includes(routeId);
    return activeRouteIds.has(routeId);
  };

  const sortedRoutes = useMemo(() => {
    return [...(routes || [])].filter(r => r && r.id).sort((a, b) => {
      const aId = String(a.id);
      const bId = String(b.id);
      return aId.localeCompare(bId, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [routes]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const shouldShowRotator = Boolean(trayConfig && trayConfig.addToggleForPointData);

  return (
    <div id="route-status-drawer" style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderTop: '1px solid #ddd',
      padding: '12px',
      zIndex: 1000,
      maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
      overflowY: 'auto'
    }}>
      {shouldShowRotator && (
        <StaticPointRotator
          points={points}
          activeRouteIds={activeRouteIds}
          trayConfig={trayConfig}
          mapRef={mapRef}
          onOpenPointPopup={onOpenPointPopup}
        />
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: sortedRoutes.length === 4
          ? 'repeat(4, minmax(0, 1fr))'
          : (viewportWidth >= 992
            ? 'repeat(4, minmax(0, 1fr))'
            : 'repeat(auto-fit, minmax(120px, 1fr))'),
        gap: '6px'
      }}>
        {sortedRoutes.map((route) => {
          const active = isActive(route.id);
          const routeColor = route.color || '#666666';

          return (
            <div
              key={route.id}
              onClick={() => {
                if (onToggleRoute) onToggleRoute(route.id);
              }}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '6px 8px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #eee',
                fontSize: '11px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: active ? 1 : 0.65
              }}
              title={active ? 'Click to hide vehicles for this route' : 'Click to show vehicles for this route'}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                <div style={{ fontWeight: 'bold', color: '#333', fontSize: '14px', lineHeight: '20px' }}>
                  {route.id}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '36px',
                    height: '20px',
                    flex: '0 0 36px'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    role="switch"
                    checked={active}
                    onChange={() => {
                      if (onToggleRoute) onToggleRoute(route.id);
                    }}
                    style={{
                      opacity: 0,
                      width: 0,
                      height: 0,
                      position: 'absolute'
                    }}
                  />
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: active ? routeColor : '#ccc',
                      transition: '0.2s',
                      borderRadius: '999px'
                    }}
                  />
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      content: '""',
                      height: '16px',
                      width: '16px',
                      left: active ? '18px' : '2px',
                      top: '2px',
                      backgroundColor: 'white',
                      transition: '0.2s',
                      borderRadius: '50%',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RouteStatusDrawer;
